import {
  Badge,
  Box,
  Card,
  CardBody,
  Center,
  Flex,
  Heading,
  Icon,
  Progress,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiClock } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useGoToEventTab } from "../useGoToEventTab";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { TenderRoundNotice } from "../rounds/TenderRoundNotice";
import { type SourcingEventDto, useListBidsForEventQuery } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { requireBooleanEnvVar } from "../../../config";
import { rowOpenProps } from "../../../common/clickableRow";
import { BidSubjectProvider } from "../../../common/terminology/bidSubjectTerms";
import { bidSubjectOf } from "../qualification/bidSubject";
import { useFocusSubjectParam } from "../../../common/focus/useFocusSubjectParam";
import { BidFocusOverlay } from "../focus/BidFocusOverlay";
import { MinimumRequirementsFocusActions } from "../focus/MinimumRequirementsFocusActions";
import { MinimumRequirementsFocusBody } from "../focus/MinimumRequirementsFocusBody";
import { useMinimumRequirementSubjects } from "../focus/useFocusSubjects";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { urls } from "../../../urls";
import { isBidContentUnsealed } from "../bidAccess";
import { EarlyAccessNote } from "../EarlyAccessNote";
import { effectiveBidDeadline } from "../qualification/stage";
import { isBidDeadlinePassed } from "../supplierRows";
import {
  deriveMinimumRequirementReview,
  type MinimumRequirementRow,
} from "./deriveMinimumRequirementReview";
import { subjectAnchor } from "../revealAnchor";
import { allowsPartIIDialogue, standingTendersAtStage } from "../rounds/partIIDialogue";
import { NotInRoundBadge } from "../evaluation/NotInRoundBadge";

interface Props {
  event: SourcingEventDto;
}

export const MinimumRequirementsTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const deadlinePassed = isBidDeadlinePassed(event);
  const unsealed = isBidContentUnsealed(event, selectedOrg);
  const { data, isLoading } = useListBidsForEventQuery(
    { eventId: event.id },
    { skip: !unsealed },
  );

  const { tenderRound, rounds } = useRoundSelection();
  const bids = useMemo(
    () =>
      tenderRound && allowsPartIIDialogue(event)
        ? standingTendersAtStage(data?.bids ?? [], rounds, tenderRound)
        : (data?.bids ?? []).filter((bid) => (bid.forNegotiationRound ?? null) === (tenderRound?.id ?? null)),
    [event, data, rounds, tenderRound],
  );
  const earlierTenderIds = useMemo(
    () =>
      new Set(
        bids.filter((bid) => (bid.forNegotiationRound ?? null) !== (tenderRound?.id ?? null)).map((bid) => bid.id),
      ),
    [bids, tenderRound],
  );
  const summary = useMemo(
    () => deriveMinimumRequirementReview(event.eventData.minimumRequirements ?? [], bids),
    [event.eventData.minimumRequirements, bids],
  );

  if (!unsealed) {
    const deadline = effectiveBidDeadline(event);
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody py="10">
          <Flex direction="column" align="center" gap="3" textAlign="center">
            <Icon as={FiClock} boxSize="8" color="smPrimary" />
            <Text fontWeight="semibold" color="smText">
              {t("The competition is open for tenders")}
            </Text>
            {deadline && (
              <Text fontSize="sm" color="smMuted">
                {t("Deadline for tenders")}: <b>{displayDateWithTime(deadline)}</b>
              </Text>
            )}
            <Text fontSize="xs" color="smMuted" maxW="md">
              {t("The minimum requirements are checked once the deadline has passed and the bids are unsealed.")}
            </Text>
          </Flex>
        </CardBody>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }

  if (summary.rows.length === 0) {
    return (
      <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
        <CardBody py="10" textAlign="center">
          <Text color="smMuted">
            {deadlinePassed ? t("No bids were received.") : t("No bids delivered yet.")}
          </Text>
        </CardBody>
      </Card>
    );
  }

  const headingText =
    summary.pending > 0
      ? t("{{reviewed}} of {{total}} bids fully reviewed", {
          reviewed: summary.assessed,
          total: summary.assessable,
        })
      : summary.notFulfilled === 0
        ? t("All {{total}} fulfilled", { total: summary.fulfilled })
        : t("{{fulfilled}} fulfilled, {{notFulfilled}} not fulfilled", {
            fulfilled: summary.fulfilled,
            notFulfilled: summary.notFulfilled,
          });

  return (
    <Stack spacing="5">
      <TenderRoundNotice />
      {!deadlinePassed && <EarlyAccessNote />}
      <Box>
        <Heading as="h3" size="sm" color="smText" mb="2">
          {headingText}
        </Heading>
        <Progress
          value={summary.assessable > 0 ? (summary.assessed / summary.assessable) * 100 : 0}
          aria-label={String(t("Progress"))}
          size="sm"
          rounded="full"
          colorScheme="teal"
          mb="3"
          maxW="sm"
          sx={{ "& > div": { borderRadius: "full" } }}
        />
        <BidSubjectProvider value={bidSubjectOf(event)}>
          <ReviewTable
            eventId={event.id}
            eventTitle={event.eventData.title}
            rows={summary.rows}
            earlierTenderIds={earlierTenderIds}
          />
        </BidSubjectProvider>
      </Box>
    </Stack>
  );
};

const ReviewTable = ({
  eventId,
  eventTitle,
  rows,
  earlierTenderIds,
}: {
  eventId: string;
  eventTitle?: string;
  rows: MinimumRequirementRow[];
  earlierTenderIds: ReadonlySet<string>;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goToTab = useGoToEventTab();
  const focusEnabled = requireBooleanEnvVar("VITE_ENABLE_FOCUS_MODE");
  const focus = useFocusSubjectParam(rows, (row) => row.bidId, {
    enabled: focusEnabled,
    isReady: rows.length > 0,
  });
  const { subjects, progress } = useMinimumRequirementSubjects(rows);
  const allAssessed = rows.length > 0 && rows.every((row) => row.state !== "PENDING");
  const focused = focus.active ? subjects[focus.index] : null;

  const openRow = (bidId: string) =>
    focusEnabled
      ? focus.open(bidId)
      : navigate(`${urls.app.sourcingEvents.bidDetail.go(eventId, bidId)}?tab=minimumRequirements`);

  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
      <Table size="md">
        <Thead>
          <Tr>
            <Th>{t("Supplier")}</Th>
            <Th>{t("Minimum requirements reviewed")}</Th>
            <Th>{t("Status")}</Th>
            <Th aria-hidden />
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr
              key={row.bidId}
              id={subjectAnchor(row.bidId)}
              _hover={{ bg: "smBackgroundSecondary" }}
              {...rowOpenProps(() => openRow(row.bidId))}
            >
              <Td>
                <Flex align="center" gap="2">
                  <Text fontWeight="semibold" fontSize="sm" color="smText" noOfLines={1}>
                    {row.supplierName}
                  </Text>
                  <NotInRoundBadge notInRound={earlierTenderIds.has(row.bidId)} />
                </Flex>
              </Td>
              <Td
                fontSize="sm"
                color={row.reviewed === row.total ? "smText" : "smMuted"}
                fontFamily="mono"
              >
                {row.total > 0 ? `${row.reviewed}/${row.total}` : "–"}
              </Td>
              <Td>
                <StateBadge state={row.state} />
              </Td>
              <Td textAlign="right">
                <Icon as={FiChevronRight} color="smMuted" />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {focused && (
        <BidFocusOverlay
          eyebrow={String(t("Minimum requirements review"))}
          subtitle={eventTitle}
          subjects={subjects}
          active={focused}
          index={focus.index}
          progress={progress}
          hasPrevious={focus.hasPrevious}
          hasNext={focus.hasNext}
          onSelect={focus.select}
          onPrevious={focus.goPrevious}
          onNext={focus.goNext}
          onClose={focus.close}
          // Keyed per bid: the review editor queues edits by requirement alone, which repeats across bids.
          renderContent={(subject) => (
            <MinimumRequirementsFocusBody key={subject.id} eventId={eventId} bidId={subject.id} />
          )}
          renderActions={(subject) => (
            <MinimumRequirementsFocusActions
              key={subject.id}
              eventId={eventId}
              bidId={subject.id}
              onRejected={focus.goNext}
              allAssessed={allAssessed}
              onGoToEvaluation={() => goToTab("evaluation")}
            />
          )}
        />
      )}
    </Box>
  );
};

const StateBadge = ({ state }: { state: MinimumRequirementRow["state"] }) => {
  const { t } = useTranslation();
  if (state === "REJECTED") return <Badge colorScheme="red">{t("Rejected")}</Badge>;
  if (state === "NOT_FULFILLED") return <Badge colorScheme="orange">{t("Deviation")}</Badge>;
  if (state === "FULFILLED") return <Badge colorScheme="green">{t("Fulfilled")}</Badge>;
  return <Badge colorScheme="gray">{t("Under review")}</Badge>;
};
